import React from "react";
import { createPortal } from "react-dom";
import { Check, X, Scale, ArrowLeftRight } from "lucide-react";
import { imgUrl } from "@/config";
import { attrLabel, featureLabel, ratingOf, fmtAge, disneyApproved, COMPARE_ROWS, OPTS } from "@/utils/strollerAttrs";

const Dots = ({ field, value }) => {
  const total = OPTS[field]?.length || 4;
  const n = ratingOf(field, value);
  if (!n) return <span className="text-[#B9C8D5]/60">—</span>;
  return (
    <span className="inline-flex items-center gap-1" title={attrLabel(field, value)}>
      <span className="flex gap-0.5">
        {Array.from({ length: total }).map((_, i) => (
          <span key={i} className={`w-1.5 h-1.5 rounded-full ${i < n ? "bg-[#FFD166]" : "bg-white/15"}`} />
        ))}
      </span>
      <span className="text-[11px] text-[#F6F1E4]/85">{attrLabel(field, value)}</span>
    </span>
  );
};

const Cell = ({ row, p }) => {
  const a = p.stroller_attributes || {};
  switch (row.type) {
    case "lbs": {
      const v = Number(a[row.key]);
      return v > 0 ? <span className="text-[#F6F1E4]">{v} lbs</span> : <span className="text-[#B9C8D5]/60">—</span>;
    }
    case "seats": {
      const v = Number(a.seats);
      return v ? <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#FFD166]/15 text-[#FFD166] text-[11px] font-medium">{v} {v === 1 ? "child" : "children"}</span> : <span className="text-[#B9C8D5]/60">—</span>;
    }
    case "age":
      return <span className="text-[#F6F1E4]">{fmtAge(a)}</span>;
    case "enum": {
      const l = attrLabel(row.key, a[row.key]);
      return l === "—" ? <span className="text-[#B9C8D5]/60">—</span> : <span className="inline-flex px-2 py-0.5 rounded-full border border-[#FFD166]/30 text-[11px] text-[#F6F1E4]">{l}</span>;
    }
    case "rate":
      return <Dots field={row.key} value={a[row.key]} />;
    case "bool":
      return a[row.key]
        ? <Check className="w-4 h-4 text-emerald-400 inline" aria-label="Yes" />
        : <X className="w-4 h-4 text-[#B9C8D5]/50 inline" aria-label="No" />;
    case "text":
      return a[row.key] ? <span className="text-[12px] text-[#F6F1E4]/90">{a[row.key]}</span> : <span className="text-[#B9C8D5]/60">—</span>;
    case "size": {
      const ok = disneyApproved(a);
      if (!a[row.key]) return <span className="text-[#B9C8D5]/60">—</span>;
      return (
        <span className="flex flex-col items-center gap-1">
          <span className="text-[12px] text-[#F6F1E4]/90">{a[row.key]}</span>
          {ok === true && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 whitespace-nowrap">Disney park approved ✓</span>
          )}
          {ok === false && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#F4A93A]/15 border border-[#F4A93A]/40 text-[#F4A93A] whitespace-nowrap">Over Disney's 31″×52″ limit</span>
          )}
        </span>
      );
    }
    case "features": {
      const feats = (a.special_features || []).slice(0, 5);
      if (!feats.length && !a.standout_features) return <span className="text-[#B9C8D5]/60">—</span>;
      return (
        <span className="flex flex-wrap gap-1 justify-center">
          {feats.map((f) => (
            <span key={f} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FFD166]/10 border border-[#FFD166]/25 text-[#FFD166]">{featureLabel(f)}</span>
          ))}
          {a.standout_features && <span className="text-[10px] text-[#F6F1E4]/85 w-full mt-0.5">{a.standout_features}</span>}
        </span>
      );
    }
    default:
      return null;
  }
};

export const CompareModal = ({ products, onClose, onRemove }) => {
  if (!products || products.length < 2) return null;
  const [a, b] = products;
  return createPortal(
    <div className="fixed inset-0 z-[120] bg-[#040A0F]/92 backdrop-blur-md flex items-center justify-center p-2 sm:p-4" onClick={onClose} data-testid="compare-modal">
      <div className="w-full max-w-3xl rounded-2xl border border-[#FFD166]/30 bg-[#0A1A26] shadow-[0_25px_80px_-10px_rgba(0,0,0,0.9)] max-h-[calc(100dvh-1rem)] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-[#FFD166]/15 shrink-0">
          <div className="flex items-center gap-2 text-[#FFD166]">
            <ArrowLeftRight className="w-4 h-4" />
            <span className="font-serif text-xl">Stroller comparison</span>
          </div>
          <button onClick={onClose} className="text-[#F6F1E4] hover:text-[#FFD166] p-2 rounded-full bg-black/40" data-testid="compare-modal-close" aria-label="Close comparison">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto overscroll-contain">
          {/* Header cards */}
          <div className="grid grid-cols-[minmax(90px,1.2fr)_2fr_2fr] gap-2 px-3 sm:px-6 pt-4 sticky top-0 bg-[#0A1A26] z-10 pb-3 border-b border-[#FFD166]/10">
            <div />
            {[a, b].map((p) => (
              <div key={p.id} className="text-center" data-testid={`compare-head-${p.id}`}>
                <img src={imgUrl(p.image)} alt={p.name} className="w-full aspect-[4/3] object-contain rounded-lg bg-black/30" />
                <div className="font-serif text-sm sm:text-base text-[#F6F1E4] leading-tight mt-1.5">{p.name}</div>
                <div className="text-[11px] text-[#FFD166]">from ${Math.floor(p.tier_8_plus ?? p.tier_1_3 ?? 0)}/day</div>
              </div>
            ))}
          </div>

          {/* Attribute rows */}
          <div className="px-3 sm:px-6 pb-6">
            {COMPARE_ROWS.map((row, i) => (
              <div key={row.label} className={`grid grid-cols-[minmax(90px,1.2fr)_2fr_2fr] gap-2 items-center py-2 ${i % 2 ? "bg-white/[0.02]" : ""} border-b border-white/5`} data-testid={`compare-row-${i}`}>
                <div className="text-[11px] uppercase tracking-wider text-[#B9C8D5] leading-tight">{row.label}</div>
                <div className="text-center text-sm"><Cell row={row} p={a} /></div>
                <div className="text-center text-sm"><Cell row={row} p={b} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

/* Sticky bottom bar shown while the shopper is building a comparison. */
export const CompareBar = ({ products, onRemove, onCompare, onClear }) => {
  if (!products.length) return null;
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[calc(100%-1.5rem)] max-w-md" data-testid="compare-bar">
      <div className="rounded-2xl border border-[#FFD166]/45 bg-[#0A2438]/95 backdrop-blur-md shadow-[0_12px_40px_rgba(0,0,0,0.6)] px-3 py-2.5 flex items-center gap-2">
        <Scale className="w-4 h-4 text-[#FFD166] shrink-0" />
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {products.map((p) => (
            <div key={p.id} className="relative shrink-0" data-testid={`compare-chip-${p.id}`}>
              <img src={imgUrl(p.image)} alt={p.name} className="w-10 h-10 rounded-lg object-cover border border-[#FFD166]/30" />
              <button type="button" onClick={() => onRemove(p.id)} aria-label={`Remove ${p.name}`} data-testid={`compare-remove-${p.id}`}
                className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#F4A93A] text-[#040A0F] flex items-center justify-center">
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
          {products.length === 1 && (
            <span className="text-[11px] text-[#B9C8D5] leading-tight">Pick 1 more stroller to compare</span>
          )}
        </div>
        <button type="button" onClick={onClear} className="text-[10px] text-[#B9C8D5] hover:text-[#FFD166] shrink-0" data-testid="compare-clear">Clear</button>
        <button
          type="button"
          onClick={onCompare}
          disabled={products.length < 2}
          className="shrink-0 text-xs font-medium rounded-full px-4 py-2 bg-[#FFD166] text-[#040A0F] disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 transition-all"
          data-testid="compare-now-btn"
        >
          Compare
        </button>
      </div>
    </div>
  );
};
